import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetProjectVersionByIdParams,
  GetProjectVersionByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetProjectVersionByIdState
  extends MergeRequestState<GetProjectVersionByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getProjectVersionById,
  isFetching: (state: ReduxState) =>
    state.server.getProjectVersionById.isFetching,
  data: (state: ReduxState) => state.server.getProjectVersionById.data,
  error: (state: ReduxState) => state.server.getProjectVersionById.error,
};

const initialState: GetProjectVersionByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getProjectVersionById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: GetProjectVersionByIdParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getProjectVersionById(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetProjectVersionById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getProjectVersionById: ServerGetProjectVersionById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
